/**
 * `proformas.*` — list, get, create, update, finalize, convert, expire,
 * delete. The CRUD-shaped surface for the proforma module (ADR-0032).
 *
 * A proforma is a preliminary, NON-FISCAL sale document. The wire shapes
 * deliberately reuse the invoice line shapes (`lineItem`, `invoiceLineRow`,
 * `taxTreatment`) so a proforma quotes exactly what the converted tax invoice
 * will charge. The fiscal procedures live on `invoices.*`; the only fiscal
 * action here is `convert`, which produces a real tax invoice.
 *
 * @see docs/adr/0032-proforma-invoices.md
 */
import { z } from "zod";
import { isoDate, moneyPaise, paged, uuid } from "../_common.js";
import { lineItem, invoiceLineRow, taxTreatment } from "./invoices.js";

/** GSTIN state code prefix — two digits. */
const stateCode = z.string().regex(/^\d{2}$/, "place of supply is a 2-digit GSTIN state code");
const currencyCode = z.string().regex(/^[A-Z]{3}$/, "currency must be a 3-letter ISO code");
const foreignMinor = z.string().regex(/^\d+$/, "foreign amount must be a non-negative integer string");
const fxRatePaise = z.string().regex(/^\d+$/, "FX rate must be paise per one foreign currency unit");

/** Proforma status — its own lifecycle (NOT the invoice one). */
export const proformaStatus = z.enum(["draft", "finalized", "converted", "expired"]);

const fxSnapshotInput = z.object({
  currencyCode: currencyCode.default("INR"),
  foreignAmountMinor: foreignMinor.optional(),
  fxRateToInrPaise: fxRatePaise.optional(),
  fxRateDate: isoDate.optional(),
  taxTreatment: taxTreatment.optional(),
});

// ── create ──────────────────────────────────────────────────────────

/** Inner shape — kept un-refined so `update` can `.extend()` it. */
const createInputObject = z
  .object({
    customerId: uuid,
    issuedOn: isoDate,
    /** Quote validity date. Optional. */
    expiresOn: isoDate.optional(),
    placeOfSupply: stateCode,
    lines: z.array(lineItem).min(1),
    customerNote: z.string().max(2000).optional(),
    internalNote: z.string().max(2000).optional(),
    poReference: z.string().max(64).optional(),
    declaration: z.string().max(4000).optional(),
  })
  .merge(fxSnapshotInput);

export const createInput = createInputObject;

export const proformaRow = z.object({
  id: uuid,
  /** Empty string for drafts; allocated `PRO/2026-27/0001` after finalize. */
  docNumber: z.string(),
  customerId: uuid,
  issuedOn: isoDate,
  expiresOn: isoDate.nullable(),
  subtotalPaise: moneyPaise,
  cgstPaise: moneyPaise,
  sgstPaise: moneyPaise,
  igstPaise: moneyPaise,
  totalPaise: moneyPaise,
  currencyCode,
  foreignAmountMinor: foreignMinor.nullable(),
  fxRateToInrPaise: fxRatePaise.nullable(),
  fxRateDate: isoDate.nullable(),
  taxTreatment,
  placeOfSupply: stateCode,
  status: proformaStatus,
  /** The tax invoice this proforma converted into, if any. */
  convertedToInvoiceId: uuid.nullable(),
  customerNote: z.string().nullable(),
  internalNote: z.string().nullable(),
  poReference: z.string().nullable(),
  declaration: z.string().nullable(),
});

export const createOutput = z.object({
  proforma: proformaRow,
  lines: z.array(invoiceLineRow),
});

// ── update ──────────────────────────────────────────────────────────

export const updateInput = createInputObject.extend({ proformaId: uuid });
export const updateOutput = createOutput;

// ── import (batch create drafts from CSV/XLSX) ────────────────────────

/**
 * One import row = one draft proforma (with all its lines inline). The client
 * parses the CSV/XLSX, groups rows by `sourceRef`, and posts one `importRow`
 * per resulting proforma. The server treats each row as a standalone
 * `createProforma` — customer must exist, dates + place-of-supply are
 * validated, lines are already normalised to milli/paise/bps.
 *
 * `sourceRef` is a human-supplied idempotency key (e.g. the caller's legacy
 * proforma number). Rows that duplicate a `sourceRef` already imported into
 * the same workspace are skipped with `duplicate_source_ref`. Non-fiscal:
 * no PRO number allocated, no ledger, no receivable.
 */
export const importRow = createInputObject.extend({
  /** Idempotency key from the source system (persisted as `po_reference`). */
  sourceRef: z.string().min(1).max(60),
});

export const importInput = z.object({
  /** At least one, at most 500 rows per batch — V1 sync cap. */
  rows: z.array(importRow).min(1).max(500),
});

export const importOutput = z.object({
  imported: z.number().int().nonnegative(),
  skipped: z.array(
    z.object({
      /** 0-based index into `importInput.rows`. */
      rowIndex: z.number().int().nonnegative(),
      reason: z.enum([
        "duplicate_source_ref",
        "customer_not_found",
        "validation",
        "internal_error",
      ]),
      /** Human-readable summary for the toast / detail sheet. */
      message: z.string(),
    }),
  ),
});

// ── finalize ────────────────────────────────────────────────────────

export const finalizeInput = z.object({ proformaId: uuid });
export const finalizeOutput = createOutput;

// ── convert ─────────────────────────────────────────────────────────

/**
 * Turn a finalized proforma into a real tax invoice. Optional date /
 * warehouse overrides for the resulting invoice; they default server-side
 * (today / today+15 / company-default warehouse).
 */
export const convertInput = z.object({
  proformaId: uuid,
  issuedOn: isoDate.optional(),
  dueOn: isoDate.optional(),
  warehouseId: uuid.nullable().optional(),
});

export const convertOutput = z.object({
  /** `issued` — invoice produced; `awaiting_approval` — a discount chain gated it. */
  outcome: z.enum(["issued", "awaiting_approval"]),
  proforma: proformaRow,
  /** Issued tax invoice id; null while awaiting approval. */
  invoiceId: uuid.nullable(),
  invoiceDocNumber: z.string().nullable(),
  approvalId: uuid.nullable(),
});

// ── expire ──────────────────────────────────────────────────────────

export const expireInput = z.object({ proformaId: uuid });
export const expireOutput = z.object({ proforma: proformaRow });

// ── delete ──────────────────────────────────────────────────────────

export const deleteInput = z.object({ proformaId: uuid });
export const deleteOutput = z.object({
  proformaId: uuid,
  customerId: uuid,
  totalPaise: moneyPaise,
});

// ── get ─────────────────────────────────────────────────────────────

export const getInput = z.object({ proformaId: uuid });

export const getOutput = z.object({
  proforma: proformaRow,
  lines: z.array(invoiceLineRow),
  customer: z.object({
    id: uuid,
    name: z.string(),
    gstin: z.string().nullable(),
    state: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    addressLine1: z.string().nullable(),
    addressLine2: z.string().nullable(),
    city: z.string().nullable(),
    country: z.string().nullable(),
  }),
});

// ── list ────────────────────────────────────────────────────────────

export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  status: proformaStatus.optional(),
  customerId: uuid.optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
  search: z.string().max(120).optional(),
});

const listRow = z.object({
  id: uuid,
  docNumber: z.string(),
  customerId: uuid,
  customerName: z.string(),
  customerGstRegistered: z.boolean(),
  customerCity: z.string().nullable(),
  customerState: z.string().nullable(),
  customerPhone: z.string().nullable(),
  issuedOn: isoDate,
  expiresOn: isoDate.nullable(),
  totalPaise: moneyPaise,
  currencyCode,
  foreignAmountMinor: foreignMinor.nullable(),
  fxRateToInrPaise: fxRatePaise.nullable(),
  fxRateDate: isoDate.nullable(),
  status: proformaStatus,
  convertedToInvoiceId: uuid.nullable(),
});

export const listOutput = paged(listRow).extend({
  counts: z.object({
    all: z.number().int().nonnegative(),
    draft: z.number().int().nonnegative(),
    finalized: z.number().int().nonnegative(),
    converted: z.number().int().nonnegative(),
    expired: z.number().int().nonnegative(),
  }),
});

// ── Inferred types ──────────────────────────────────────────────────

export type CreateInput = z.infer<typeof createInput>;
export type CreateOutput = z.infer<typeof createOutput>;
export type UpdateInput = z.infer<typeof updateInput>;
export type UpdateOutput = z.infer<typeof updateOutput>;
export type FinalizeInput = z.infer<typeof finalizeInput>;
export type FinalizeOutput = z.infer<typeof finalizeOutput>;
export type ConvertInput = z.infer<typeof convertInput>;
export type ConvertOutput = z.infer<typeof convertOutput>;
export type ExpireInput = z.infer<typeof expireInput>;
export type ExpireOutput = z.infer<typeof expireOutput>;
export type DeleteInput = z.infer<typeof deleteInput>;
export type DeleteOutput = z.infer<typeof deleteOutput>;
export type GetInput = z.infer<typeof getInput>;
export type GetOutput = z.infer<typeof getOutput>;
export type ListInput = z.infer<typeof listInput>;
export type ListOutput = z.infer<typeof listOutput>;
export type ProformaRow = z.infer<typeof proformaRow>;
export type ProformaStatus = z.infer<typeof proformaStatus>;
export type ImportRow = z.infer<typeof importRow>;
export type ImportInput = z.infer<typeof importInput>;
export type ImportOutput = z.infer<typeof importOutput>;
